import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { useFieldArray, Controller, Control } from "react-hook-form";
import { Trash2, PlusCircle, Lightbulb } from "lucide-react";
import { FormLabel } from "@ui/internal/components/label";
import { IconPicker } from "@components/ui/icon-picker";

interface SkillsContainerProps {
  control: Control<any>;
}

const SkillsContainer = ({ control }: SkillsContainerProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "skills.values",
  });

  return (
    <div className="w-full space-y-8 mt-10">
      <div className="space-y-4">
        <Controller
          control={control}
          name="skills.eyebrow"
          render={({ field, fieldState: { error } }) => (
            <div className="space-y-1">
              <FormLabel label="Eyebrow" />
              <Input
                {...field}
                placeholder="e.g. What you'll learn"
                className="max-w-md"
              />
              {error && (
                <span className="text-xs text-red-500">{error.message}</span>
              )}
            </div>
          )}
        />

        <Controller
          control={control}
          name="skills.heading"
          render={({ field, fieldState: { error } }) => (
            <div className="space-y-1">
              <FormLabel label="Heading" />
              <Input
                {...field}
                placeholder="e.g. Skills You'll Master"
                className="max-w-xl"
              />
              {error && (
                <span className="text-xs text-red-500">{error.message}</span>
              )}
            </div>
          )}
        />
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          Skill Items
        </h3>

        <div className="space-y-4">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="p-5 rounded-md border bg-white shadow-sm relative group animate-in fade-in slide-in-from-top-2"
            >
              <div className="absolute top-4 right-4 opacity-group group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => remove(index)}
                >
                  <Trash2 size={18} />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-emerald-600 mb-2">
                  <Lightbulb size={20} />
                  <h3 className="font-semibold text-lg">Skill {index + 1}</h3>
                </div>

                <Controller
                  control={control}
                  name={`skills.values.${index}.icon`}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <FormLabel label="Skill Icon" />
                      <IconPicker
                        onValueChange={(icon) => field.onChange(icon)}
                        value={field.value}
                      />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={`skills.values.${index}.title`}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <FormLabel label="Skill Title" />
                      <Input
                        {...field}
                        placeholder="e.g. Intro to Digital Marketing"
                      />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={`skills.values.${index}.description`}
                  render={({ field, fieldState: { error } }) => (
                    <div className="space-y-1">
                      <FormLabel label="Skill Description" />
                      <Textarea
                        {...field}
                        placeholder="Enter skill details..."
                        className="min-h-[80px] resize-none"
                      />
                      {error && (
                        <span className="text-xs text-red-500">
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center pt-4">
          <Button
            type="button"
            variant="outline"
            className="flex items-center gap-2 border-[#1DA077] text-[#1DA077] hover:bg-[#1DA077] hover:text-white transition-all shadow-sm"
            onClick={() =>
              append({
                title: "",
                description: "",
                icon: "",
              })
            }
          >
            <PlusCircle size={18} />
            Add Skill
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SkillsContainer;